import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import {
  ItemCard,
  ItemCardBody,
  ItemCardMeta,
  ItemCardSection,
  ItemCardSummary,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import '@sola/locales/formatters.ts';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';
import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon.tsx';
import { mapFundCodeToCommitmentType } from '../../organisms/giving-history/giving-history.logic.ts';
import type { GivingItemWithCommitment } from '../../utils/giving-history.ts';
import {
  getHistoryItemData,
  getTaxYearForBadge,
} from './giving-history-card.logic.ts';

export type GivingHistoryCardProps = {
  givingItem: GivingItemWithCommitment;
};

const getIcon = (givingItem: GivingItemWithCommitment) => {
  if (givingItem.commitment) {
    return <CommitmentAvatar responsive commitment={givingItem.commitment} />;
  }
  if (givingItem.pledge) {
    return <PledgeIcon {...givingItem.pledge} />;
  }

  // Some giving items have enought data to craft an icon even without a commitment
  return (
    <CommitmentAvatar
      responsive
      commitment={{
        commitmentType: mapFundCodeToCommitmentType(givingItem.fundCode),
        fund: {
          code: givingItem.fundCode,
          icon: givingItem.fundIcon,
        },
      }}
    />
  );
};

const useComponentData = (args: GivingHistoryCardProps & { t: TFunction }) => {
  const { givingItem, t } = args;

  const { title, label, rate } = getHistoryItemData(givingItem, t);
  const taxYear = getTaxYearForBadge(givingItem);

  const icon = getIcon(givingItem);

  return {
    icon,
    title,
    label,
    rate,
    taxYear,
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const GivingHistoryCard: React.FC<GivingHistoryCardProps> = (props) => {
  const { t } = useTranslation(['my', 'core']);

  const cd = useComponentData({ t, ...props });

  const rate =
    typeof cd.rate === 'string'
      ? cd.rate
      : t('core:numbers.currencyCustom', {
          amount: cd.rate,
          useNegativeParens: true,
        });

  return (
    <ItemCard>
      <ItemCardSection>
        <ItemCardBody>
          <ItemCardSummary
            icon={cd.icon}
            title={cd.title}
            subTitle={cd.label}
          />
          <ItemCardMeta
            text={rate}
            badge={
              cd.taxYear ? (
                <Badge
                  label={t('my:givingHistory.taxYear', {
                    year: cd.taxYear,
                  })}
                  responsive
                  size="sm"
                />
              ) : undefined
            }
          />
        </ItemCardBody>
      </ItemCardSection>
    </ItemCard>
  );
};
